import { unwrapResult } from '@/api/openapi-client';
import type { AiChangesetApi, TurnOutcome } from '@/features/ai-changeset/hooks/useAiChangeset';
import type { DockSession } from '@/features/ai-changeset/utils/dock-history';
import type { z } from 'zod';

export type EndpointResult<T> = { data?: T; error?: unknown; response: Response };

export interface TurnEndpoints<TSession> {
  load: (before: string | undefined) => Promise<EndpointResult<TSession>>;
  apply: (turnId: string) => Promise<EndpointResult<unknown>>;
  undo: (turnId: string) => Promise<EndpointResult<unknown>>;
  discard: (turnId: string) => Promise<EndpointResult<unknown>>;
}

interface ChangesetSchemas<TChange, TSession> {
  sessionSchema: z.ZodType<{ session: DockSession<TChange> | null }, TSession>;
  staleSchema: z.ZodType<{ stale: TChange[] }>;
  appliedSchema: z.ZodType<{ applied: true; appliedCount: number; missing: TChange[] }>;
}

export const sessionOf = async <TChange, TSession>(
  schema: ChangesetSchemas<TChange, TSession>['sessionSchema'],
  request: Promise<EndpointResult<TSession>>,
): Promise<DockSession<TChange> | null> =>
  schema.parse(unwrapResult<TSession>(await request)).session;

export async function turnOutcomeOf<TChange>(
  schemas: Pick<ChangesetSchemas<TChange, unknown>, 'staleSchema' | 'appliedSchema'>,
  request: Promise<EndpointResult<unknown>>,
): Promise<TurnOutcome<TChange>> {
  const result = await request;
  const stale = schemas.staleSchema.safeParse(result.error);
  if (result.response.status === 409 && stale.success) {
    return { applied: false, stale: stale.data.stale };
  }
  return schemas.appliedSchema.parse(unwrapResult(result));
}

export const changesetApi = <TChange, TSession>(
  schemas: ChangesetSchemas<TChange, TSession>,
  endpoints: TurnEndpoints<TSession>,
): AiChangesetApi<TChange> => ({
  load: (before) => sessionOf(schemas.sessionSchema, endpoints.load(before)),
  apply: (turnId) => turnOutcomeOf(schemas, endpoints.apply(turnId)),
  undo: (turnId) => turnOutcomeOf(schemas, endpoints.undo(turnId)),
  discard: async (turnId) => unwrapResult(await endpoints.discard(turnId)),
});
